import React from "react";
import { CustomSelect } from "./CustomSelect";

interface Option {
  value: string | number;
  label: string;
  subLabel?: string;
  /** When true, the option is rendered as a placeholder (muted text, not a real selectable value). */
  isPlaceholder?: boolean;
  /** Renders this option's label in bold (legacy emphasis for seeded entries). */
  isBold?: boolean;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string | React.ReactNode;
  /** Plain-string title for the label element tooltip. Required when `label` is JSX (not a string). */
  labelTitle?: string;
  labelClassName?: string;
  options: Option[];
  "data-testid"?: string;
  useNative?: boolean; // Allow opting into native select if needed
  direction?: 'up' | 'down';
  buttonClassName?: string;
  searchable?: boolean;
  showOptionTooltips?: boolean;
  /**
   * QA #2421/#2423/#2425 — when provided, each dropdown option is rendered as
   * a hyperlink. Plain click selects, Cmd/Ctrl/middle-click opens in a new
   * tab. Forwarded to CustomSelect (the non-native render path).
   */
  getItemUrl?: (option: Option) => string | undefined | null;
  /** Accessible label forwarded to the underlying button/select element. */
  "aria-label"?: string;
  /** ID of an external label element to associate with the underlying button/select element via aria-labelledby. */
  "aria-labelledby"?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ label, labelTitle, labelClassName, options, id, className, useNative = false, value, onChange, direction, buttonClassName, searchable = false, title, showOptionTooltips, getItemUrl, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-invalid": ariaInvalid, "aria-required": ariaRequired, "aria-describedby": ariaDescribedby, ...props }, ref) => {
    // If useNative is true, render the native select with theme colors
    if (useNative) {
        // Skip rendering the <label> wrapper when no label text was supplied;
        // an empty <label> fails Lighthouse's "form-elements have associated
        // labels" audit and clutters the a11y tree. Callers that need a
        // hidden label should pass `aria-label` instead.
        const hasLabel = label !== undefined && label !== null && label !== "";
        const labelTitleResolved = labelTitle ?? (typeof label === 'string' ? label : undefined);
        return (
            <div>
                {hasLabel ? (
                    <label htmlFor={id} className={labelClassName || "block text-sm font-medium text-text mb-1 whitespace-nowrap"} title={labelTitleResolved}>
                        {label}
                    </label>
                ) : null}
                <div className="mt-1">
                    <select
                            id={id}
                            className={`block w-full h-[36px] border border-border-strong rounded-[4px] shadow-sm py-[6px] px-[10px] pr-[32px] text-[14px] text-text bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-no-repeat bg-[right_10px_center] bg-[length:16px_16px] ${className || ''}`}
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`
                            }}
                            ref={ref}
                        value={value}
                        onChange={onChange}
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledby}
                        aria-invalid={ariaInvalid}
                        aria-required={ariaRequired}
                        aria-describedby={ariaDescribedby}
                        {...props}
                >
                    {options.map((option, index) => (
                        <option
                            key={`${String(option.value)}-${index}`}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                    </select>
                </div>
            </div>
        );
    }

    // Default to CustomSelect for full theme support
    return (
        <CustomSelect
            label={label}
            {...(labelTitle !== undefined ? { labelTitle } : {})}
            options={options}
            value={value as string | number}
            onChange={(newValue) => {
                // Convert CustomSelect's onChange to native select's onChange event
                if (onChange) {
                    const syntheticEvent = {
                        target: { value: newValue, name: props.name },
                        currentTarget: { value: newValue, name: props.name },
                    } as React.ChangeEvent<HTMLSelectElement>;
                    onChange(syntheticEvent);
                }
            }}
            id={id}
            className={className}
            disabled={props.disabled}
            data-testid={props["data-testid"]}
            direction={direction || 'down'}
            buttonClassName={buttonClassName || ''}
            searchable={searchable}
            title={title}
            {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
            {...(ariaLabelledby !== undefined ? { "aria-labelledby": ariaLabelledby } : {})}
            {...(ariaInvalid !== undefined ? { "aria-invalid": ariaInvalid } : {})}
            {...(ariaRequired !== undefined ? { "aria-required": ariaRequired === true || ariaRequired === "true" ? true : ariaRequired === false || ariaRequired === "false" ? false : undefined } : {})}
            {...(ariaDescribedby !== undefined ? { "aria-describedby": ariaDescribedby } : {})}
            {...(showOptionTooltips !== undefined ? { showOptionTooltips } : {})}
            {...(getItemUrl ? { getItemUrl } : {})}
        />
    );
});
Select.displayName = 'Select';
